
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CountryFlag from './CountryFlag';
import { CountryWithRegion } from '@/hooks/useCountriesWithRegions';

interface CountryRegionalViewProps {
  countries: CountryWithRegion[];
  onSelectCountry: (code: string) => void;
}

const CountryRegionalView: React.FC<CountryRegionalViewProps> = ({
  countries,
  onSelectCountry,
}) => {
  const navigate = useNavigate();

  // Group countries by region
  const groupedCountries = countries.reduce((acc, country) => {
    const regionName = country.region?.name || 'No Region';
    const regionId = country.region?.id || 'no-region';
    
    if (!acc[regionId]) {
      acc[regionId] = {
        name: regionName,
        countries: []
      };
    }
    
    acc[regionId].countries.push(country);
    return acc;
  }, {} as Record<string, { name: string; countries: CountryWithRegion[] }>);

  // Sort regions alphabetically, but put "No Region" last
  const sortedRegions = Object.entries(groupedCountries).sort(([aId, a], [bId, b]) => {
    if (aId === 'no-region') return 1;
    if (bId === 'no-region') return -1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="space-y-8">
      {sortedRegions.map(([regionId, regionData]) => (
        <div key={regionId} className="space-y-4">
          <h3 className="text-xl font-semibold text-nation-blue border-b pb-2">
            {regionData.name}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regionData.countries.map(country => (
              <Card key={country.code} className={country.active ? 'border-nation-teal' : 'border-gray-200'}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <CountryFlag countryCode={country.code} className="h-5 w-6" />
                      <CardTitle className="text-lg font-semibold text-nation-blue">
                        {country.name}
                      </CardTitle>
                    </div>
                    {country.active ? (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                        <Check size={12} className="mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full flex items-center">
                        <Clock size={12} className="mr-1" />
                        Coming Soon
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {country.active ? (
                    <Button 
                      variant="default" 
                      className="w-full bg-nation-teal hover:bg-nation-teal-dark"
                      onClick={() => onSelectCountry(country.code)}
                    >
                      View {country.name}
                    </Button>
                  ) : (
                    <Button variant="outline" className="w-full" disabled>
                      Not Available Yet
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
      
      {sortedRegions.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No countries found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

export default CountryRegionalView;
